import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/atoms";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { GettingStartedSection } from "@/redux/slices/onboarding/types";
import { SetupPanel, SetupPanelItem, DownloadModal, WalkthroughModal, ReadSetupGuideModal, WhyDoINeedModal } from "../common";
import { quickSetupContent, quickSetupExplanations } from "../data/quickSetupContent";
import { isSectionComplete } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";

const GettingStartedPaths = {
  PAYMENT_CONFIGURATION: "/payments/payment-configuration",
  BUSINESS_CONFIG: "/config/business",
} as const;

enum ModalType {
  GENERAL_DOWNLOAD = "general-download",
  GENERAL_WALKTHROUGH = "general-walkthrough",
  GENERAL_DOCUMENTATION = "general-documentation",
  GENERAL_WHY = "general-why-do-i-need",
  STRIPE_WALKTHROUGH = "stripe-walkthrough",
  STRIPE_DOCUMENTATION = "stripe-documentation",
  STRIPE_WHY = "stripe-why-do-i-need",
}

interface GettingStartedPanelProps {
  defaultOpen?: boolean;
}

export const GettingStartedPanel = ({ defaultOpen = false }: GettingStartedPanelProps) => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isStripeCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.gettingStarted.stripe) : false;
  const isGeneralWalkthroughCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.gettingStarted.generalWalkthrough) : false;

  const totalSteps = 2;
  const completedSteps = [isStripeCompleted, isGeneralWalkthroughCompleted].filter(Boolean).length;

  const handleModalClose = () => setActiveModal(null);

  const handleGeneralDownloadImage = () => {
    setActiveModal(ModalType.GENERAL_DOWNLOAD);

    if (onboardingStatus) {
      const generalWalkthrough = onboardingStatus.gettingStarted.generalWalkthrough;

      if (generalWalkthrough && !isSectionComplete(generalWalkthrough)) {
        updateSectionStatus({ section: GettingStartedSection.GENERAL_WALKTHROUGH, completed: true }).catch((error) => {
          console.error("Failed to update section status:", error);
        });
      }
    }
  };

  const handleGeneralWatchVideo = () => {
    setActiveModal(ModalType.GENERAL_WALKTHROUGH);
  };

  const handleGeneralReadDocumentation = () => {
    setActiveModal(ModalType.GENERAL_DOCUMENTATION);
  };

  const handleGeneralWhyDoThis = () => {
    setActiveModal(ModalType.GENERAL_WHY);
  };

  const handleStripeWatchVideo = () => {
    setActiveModal(ModalType.STRIPE_WALKTHROUGH);
  };

  const handleStripeReadDocumentation = () => {
    setActiveModal(ModalType.STRIPE_DOCUMENTATION);
  };

  const handleStripeWhyDoThis = () => {
    setActiveModal(ModalType.STRIPE_WHY);
  };

  const handleStripeSetup = () => {
    setQuickSetupFlag(GettingStartedSection.STRIPE);
    navigate(GettingStartedPaths.PAYMENT_CONFIGURATION);

    if (onboardingStatus) {
      const stripeSection = onboardingStatus.gettingStarted.stripe;
      if (stripeSection && !isSectionComplete(stripeSection)) {
        updateSectionStatus({ section: GettingStartedSection.STRIPE, completed: true }).catch((error) => {
          console.error("Failed to update section status:", error);
        });
      }
    }
  };

  const generalFeaturedIcon = {
    name: "help-circle",
    background: "bg-primary-light",
    variant: "Outline" as const,
    isCustom: true,
  };

  const stripeFeaturedIcon = {
    name: "stripe",
    background: "bg-[#6772E5]",
    isCustom: true,
  };

  return (
    <SetupPanel
      stepNumber={1}
      title="Getting Started"
      description="Get started by setting up your stripe in the payments page"
      stepCount={totalSteps}
      completedSteps={completedSteps}
      defaultOpen={defaultOpen}
    >
      {/* <SetupPanelItem
        icon="LampCharge"
        iconBackground="bg-neutral-grey"
        title="About You & Your Business"
        isCompleted={true}
        actions={[
          {
            label: "View Answers",
            onClick: () => navigate(GettingStartedPaths.BUSINESS_CONFIG),
            variant: "secondary",
          },
        ]}
      /> */}

      <SetupPanelItem
        icon={generalFeaturedIcon.name}
        iconBackground={generalFeaturedIcon.background}
        isCustomIcon={generalFeaturedIcon.isCustom}
        iconVariant={generalFeaturedIcon.variant}
        title="General Walkthrough"
        isCompleted={isGeneralWalkthroughCompleted}
        actions={[
          {
            label: "Download Image",
            onClick: handleGeneralDownloadImage,
            variant: "primary",
          },
        ]}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleGeneralWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleGeneralWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleGeneralReadDocumentation },
        ]}
      />

      <SetupPanelItem
        icon={stripeFeaturedIcon.name}
        iconBackground={stripeFeaturedIcon.background}
        isCustomIcon={stripeFeaturedIcon.isCustom}
        title="Setup your Stripe Settings"
        isCompleted={isStripeCompleted}
        actions={[
          {
            label: "Setup Stripe",
            onClick: handleStripeSetup,
            variant: "primary",
          },
        ]}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleStripeWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleStripeWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleStripeReadDocumentation },
        ]}
      />

      <DownloadModal
        featuredIcon={generalFeaturedIcon}
        open={activeModal === ModalType.GENERAL_DOWNLOAD}
        onClose={handleModalClose}
        title="General Walkthrough"
        imageUrl={quickSetupContent.general.walkthrough.imageUrl}
        downloadImageUrl={quickSetupContent.general.walkthrough.downloadImageUrl}
      />

      {/* General Walkthrough Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.GENERAL_WHY}
        onClose={handleModalClose}
        title="Why do I need to complete this walkthrough?"
        featuredIcon={generalFeaturedIcon}
        explanations={quickSetupExplanations.general.walkthrough}
      />

      <WalkthroughModal
        featuredIcon={generalFeaturedIcon}
        open={activeModal === ModalType.GENERAL_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (General)"
        videoUrl={quickSetupContent.general.walkthrough.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={generalFeaturedIcon}
        open={activeModal === ModalType.GENERAL_DOCUMENTATION}
        onClose={handleModalClose}
        title="Getting Started Setup Guide"
        documentationUrl={quickSetupContent.general.walkthrough.documentationUrl}
        pdfUrl={quickSetupContent.general.walkthrough.pdfUrl}
      />

      {/* Stripe Modals */}
      <WalkthroughModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Stripe Setup)"
        videoUrl={quickSetupContent.stripe.setup.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_DOCUMENTATION}
        onClose={handleModalClose}
        title="Stripe Setup Guide"
        documentationUrl={quickSetupContent.stripe.setup.documentationUrl}
        pdfUrl={quickSetupContent.stripe.setup.pdfUrl}
      />

      <WhyDoINeedModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_WHY}
        onClose={handleModalClose}
        title="Why do I need to setup Stripe?"
        explanations={quickSetupExplanations.stripe.setup}
      />
    </SetupPanel>
  );
};
